/**
 * AQ-Cloud2 global status bar (32px, sticky bottom, contextual).
 *
 * Three zones:
 *   left   — context summary (counts, selection)
 *   center — live totals (currency-aware, tabular figures)
 *   right  — system state (save indicator, lock indicator, env badge)
 *
 * On narrow viewports (<1280px) the center totals collapse to a popover
 * pill via `collapsedCenter`. Pages don't render `<StatusBar />` directly —
 * they declare zone content via `useStatusBar()` and the shell renders it.
 *
 * See design-mockups/status-bar-detail.html for the visual spec.
 */
import React from 'react'
import { useStatusBar } from '@/components/layout/StatusBarProvider'
import { cn } from '@/lib/utils'

interface StatusBarProps {
  className?: string
}

export const StatusBar = ({ className }: StatusBarProps): JSX.Element | null => {
  const { visible, contextSummary, totals, state, collapsedCenter } = useStatusBar()

  if (!visible) return null

  return (
    <footer
      className={cn(
        'h-8 bg-card border-t border-border sticky bottom-0 z-30',
        'flex items-center px-4 text-xs',
        className,
      )}
      role="status"
      aria-live="polite"
      aria-label="Application status"
    >
      <div className="flex items-center gap-3 text-muted-foreground min-w-0">
        {contextSummary}
      </div>
      <div className="flex-1 flex items-center justify-center gap-4 px-4 min-w-0">
        {/* xl screens show the full totals; smaller viewports show the pill. */}
        <div className="hidden xl:flex items-center gap-4 num">{totals}</div>
        <div className="xl:hidden">{collapsedCenter ?? totals}</div>
      </div>
      <div className="flex items-center gap-3 text-muted-foreground min-w-0">
        {state}
      </div>
    </footer>
  )
}
